import React from 'react';
import { MultiSelect } from '@mantine/core';

export interface AfaMultiSelectProps {
  /** Datos para el multiselect - array de strings o objeto        },
        ...styles,
      }}
      className={className}
      {...multiselectProps}
    />
  );
}lue/label */
  data: string[] | { value: string; label: string }[];
  
  /** Valores seleccionados actuales */
  value?: string[];
  
  /** Callback cuando cambian las selecciones */
  onChange?: (value: string[]) => void;
  
  /** Placeholder del input */
  placeholder?: string;
  
  /** Label del campo */
  label?: string;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si es requerido */
  required?: boolean;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Tamaño del input */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Filtrable por texto */
  searchable?: boolean;
  
  /** Número máximo de opciones a mostrar */
  limit?: number;
  
  /** Máximo número de valores seleccionables */
  maxSelectedValues?: number;
  
  /** Texto cuando no hay resultados */
  nothingFound?: string;
  
  /** Ocultar valores seleccionados del dropdown */
  hidePickedOptions?: boolean;
  
  /** Nombre del campo para formularios */
  name?: string;
  
  /** ID del campo */
  id?: string;
  
  /** Props adicionales para el MultiSelect */
  multiSelectProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
}

/**
 * Wrapper del MultiSelect de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico con array de strings
 * <AfaMultiSelect
 *   data={['React', 'Angular', 'Vue', 'Svelte']}
 *   placeholder="Selecciona frameworks"
 *   label="Frameworks JavaScript"
 * />
 * 
 * // Con objetos value/label
 * <AfaMultiSelect
 *   data={[
 *     { value: 'react', label: 'React' },
 *     { value: 'angular', label: 'Angular' }
 *   ]}
 *   searchable
 *   placeholder="Buscar frameworks..."
 * />
 * 
 * // Controlado con máximo
 * const [values, setValues] = useState([]);
 * <AfaMultiSelect
 *   data={options}
 *   value={values}
 *   onChange={setValues}
 *   maxSelectedValues={3}
 * />
 */
export function AfaMultiSelect({
  data,
  value,
  onChange,
  placeholder = 'Seleccioni opció',
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = 'sm',
  radius = 'md',
  searchable = true,
  limit = 10,
  maxSelectedValues,
  nothingFound = 'No s\'han trobat resultats',
  hidePickedOptions = false,
  name,
  id,
  multiSelectProps,
  styles,
  className,
}: AfaMultiSelectProps) {

  // Normalizar datos a formato string[] (MultiSelect puede usar strings directamente)
  const normalizedData = data.map(item => 
    typeof item === 'string' 
      ? item
      : item.value // MultiSelect usa el value, no el label
  );

  return (
    <MultiSelect
      data={normalizedData}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      label={label}
      description={description}
      error={error}
      required={required}
      name={name}
      id={id}
      disabled={disabled}
      size={size}
      radius={radius}
      searchable={searchable}
      limit={limit}
      maxSelectedValues={maxSelectedValues}
      nothingFoundMessage={nothingFound}
      hidePickedOptions={hidePickedOptions}
      styles={{
        input: {
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: '0 0 0 2px var(--mantine-color-brand-1)',
          },
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 500,
        },
        dropdown: {
          borderColor: 'var(--mantine-color-brand-3)',
          // Media query removida para evitar errores CSS-in-JS
        },
        option: {
          '&[data-selected]': {
            backgroundColor: 'var(--mantine-color-brand-0)',
            color: 'var(--mantine-color-brand-8)',
          },
          '&:hover': {
            backgroundColor: 'var(--mantine-color-brand-1)',
          },
        },
        pill: {
          backgroundColor: 'var(--mantine-color-brand-1)',
          color: 'var(--mantine-color-brand-8)',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-brand-2)',
          },
        },
        ...styles,
      }}
      {...(className && { className })}
      {...multiSelectProps}
    />
  );
}